import { isFuseHolderVariant } from "../auto-fuse";
import { computeBatteryStacks } from "../battery-stack";
import type { DefLookup } from "../catalog";
import {
  FUSE_AMP_RANGE,
  batteryFaultCurrent,
  type ElectricalEvidence,
  FUSE_REFERENCE_PROFILES,
  fuseAicAtVoltage,
  fuseTypeDisplayName,
  resolveCableFuseType,
} from "../fuse-aic";
import { indexNodesById } from "../node-tree";
import { buildDcCircuitTopology, dcInputPorts } from "../dc-circuit-topology";
import { resolveProtectionDevices, type ProtectionDevice } from "../protection-devices";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  FuseType,
  Issue,
  System,
} from "../types";
import { type Vertex } from "./graph";

/** Fault evidence belongs to a pair of source rails, never a positive rail alone. */
export type BatteryGroup = {
  batteryNodeIds: string[];
  positiveVerts: Vertex[];
  negativeVerts: Vertex[];
  /** Internal positive posts carry series-bank current too. */
  seriesVerts: Vertex[];
  faultCurrent: ElectricalEvidence;
  /** Compatibility accessor: unknown remains undefined, never a partial sum. */
  shortCircuitA?: number;
  nominalV?: number;
  unknownFaultNodeIds: string[];
};

export function hasUnknownFaultCurrent(def: Pick<ComponentDefinition, "chemistry" | "shortCircuitA" | "aiEstimatedFields" | "source">): boolean {
  return batteryFaultCurrent(def).status === "unknown";
}

// Victron Lynx Smart BMS manual rev. 17 (01/2026), §4.2.2:
// https://www.victronenergy.com/upload/documents/Lynx_Smart_BMS/109358-Lynx_Smart_BMS-pdf-en.pdf
// This manufacturer-only rule applies to a connected Lynx chain, never to a
// generic bus or fuse block. Module ratings are separate from terminal
// ratings: an unpublished stud rating must not erase a known module limit.
// Power In / Distributor: respective manuals, System Design §4.2 / §5.2.
// https://www.victronenergy.com/media/pg/Lynx_Power_In/en/system-design.html
// Class-T: §4.2.2 limits its two fused inputs to 800 A (2 × 400 A).
// https://www.victronenergy.com/media/pg/Lynx_Class-T_Power_In/en/system-design.html
// The BMS models' 600/1200 A peaks are five-minute thermal limits, not
// cranking surges, so they deliberately do not enter `ampSurge`.
const LYNX_MODULE_RATINGS: Readonly<Record<string, number>> = {
  "dist-victron-lynx-smart-bms-500": 500,
  "dist-victron-lynx-smart-bms-1000": 1000,
  "dist-victron-lynx-distributor": 1000,
  "dist-victron-lynx-power-in": 1000,
  "dist-victron-lynx-classt-power-in": 800,
};

type RatedLynxModule = {
  node: ComponentNode;
  def: ComponentDefinition;
  ratingA: number;
};

function ratedLynxModule(
  node: ComponentNode,
  defLookup: DefLookup,
): RatedLynxModule | undefined {
  const ratingA = LYNX_MODULE_RATINGS[node.definitionId];
  if (!ratingA) return undefined;
  const def = defLookup(node.definitionId, node.customDefinition);
  if (!def) return undefined;
  return { node, def, ratingA };
}

/**
 * Victron limits the sum of downstream circuit fuses on a Lynx chain to the
 * continuous rating of its lowest-rated module. Fuse ratings live on branch
 * cables; links between Lynx modules and input-side feed fuses are not branches.
 */
export function detectLynxFuseSumExceedsRating(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const modules = new Map<string, RatedLynxModule>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const rated = ratedLynxModule(node, defLookup);
    if (rated) modules.set(node.id, rated);
  }
  if (modules.size === 0) return [];

  const neighbours = new Map<string, Set<string>>();
  for (const nodeId of modules.keys()) neighbours.set(nodeId, new Set());
  for (const cable of system.cables) {
    if (!modules.has(cable.fromNodeId) || !modules.has(cable.toNodeId)) continue;
    neighbours.get(cable.fromNodeId)!.add(cable.toNodeId);
    neighbours.get(cable.toNodeId)!.add(cable.fromNodeId);
  }

  const issues: Issue[] = [];
  const visited = new Set<string>();
  for (const seed of modules.keys()) {
    if (visited.has(seed)) continue;
    const chainIds: string[] = [];
    const queue = [seed];
    visited.add(seed);
    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      chainIds.push(nodeId);
      for (const next of neighbours.get(nodeId) ?? []) {
        if (visited.has(next)) continue;
        visited.add(next);
        queue.push(next);
      }
    }

    const chain = new Set(chainIds);
    const branchCables: Cable[] = [];
    let sum = 0;
    for (const cable of system.cables) {
      const fromLynx = chain.has(cable.fromNodeId);
      const toLynx = chain.has(cable.toNodeId);
      if (fromLynx === toLynx) continue;
      const rating = cable.fuseAmpOverride;
      if (rating == null || rating <= 0) continue;
      const lynx = modules.get(fromLynx ? cable.fromNodeId : cable.toNodeId)!;
      const terminalId = fromLynx ? cable.fromTerminalId : cable.toTerminalId;
      const terminal = lynx.def.terminals.find((t) => t.id === terminalId);
      if (terminal?.polarity !== "positive" || terminal.direction === "input") {
        continue;
      }
      branchCables.push(cable);
      sum += rating;
    }

    let limiting = modules.get(chainIds[0])!;
    for (const nodeId of chainIds.slice(1)) {
      const candidate = modules.get(nodeId)!;
      if (candidate.ratingA < limiting.ratingA) limiting = candidate;
    }
    if (sum <= limiting.ratingA + 1e-9) continue;

    const moduleLabel =
      limiting.node.label ?? limiting.def.shortName ?? limiting.def.name;
    issues.push({
      code: "lynx-fuse-sum-exceeds-rating",
      severity: "critical",
      title: {
        key: "diag.lynx-fuse-sum-exceeds-rating.title",
        vars: { sum, limit: limiting.ratingA },
      },
      message: {
        key: "diag.lynx-fuse-sum-exceeds-rating.message",
        vars: { sum, limit: limiting.ratingA, module: moduleLabel },
      },
      nodeIds: chainIds,
      cableIds: branchCables.map((cable) => cable.id),
      fix: { key: "diag.lynx-fuse-sum-exceeds-rating.fix" },
    });
  }
  return issues;
}

export function getBatteryGroups(system: System, defLookup: DefLookup): BatteryGroup[] {
  const topology = buildDcCircuitTopology(system, defLookup);
  const nodes = indexNodesById(system.nodes);
  if (topology.unsupported.some(item => item.reason === "source-range-limit")) {
    const batteries = system.nodes.flatMap(node => {
      const def = node.kind === "component" ? defLookup(node.definitionId, node.customDefinition) : undefined;
      return def?.category === "battery" ? [{ id: node.id, def }] : [];
    });
    return [{ batteryNodeIds: batteries.map(b => b.id).sort(),
      positiveVerts: batteries.flatMap(b => b.def.terminals.filter(t => t.polarity === "positive").map(t => `${b.id}:${t.id}`)),
      negativeVerts: [], seriesVerts: [],
      faultCurrent: { status: "unknown", reason: "source-topology-limit" },
      unknownFaultNodeIds: batteries.map(b => b.id).sort(),
    }];
  }
  const all = topology.sources.filter(source => source.kind === "battery");
  // Contiguous ranges overlap in a series bank. Keep every pair actually used
  // by a complete consumer loop (including an upper-cell-only tap). Where no
  // return is drawn, retain the outer bank as unresolved context; never add
  // overlapping cell ranges as independent parallel contributions.
  const active = new Set(dcInputPorts(system, defLookup).flatMap(port =>
    topology.resolvePort(port).routes.map(route => route.sourceId)));
  const sources = all.filter(source => {
    if (active.has(source.id)) return true;
    const overlapping = all.filter(other => other !== source &&
      topology.railId(other.positive[0]) === topology.railId(source.positive[0]) &&
      source.batteryNodeIds.some(id => other.batteryNodeIds.includes(id)));
    if (overlapping.some(other => active.has(other.id))) return false;
    return !all.some(other => source.batteryNodeIds.length < other.batteryNodeIds.length &&
      source.batteryNodeIds.every(id => other.batteryNodeIds.includes(id)));
  });
  const groups = new Map<string, typeof sources>();
  for (const source of sources) {
    const key = `${topology.railId(source.positive[0])}|${topology.railId(source.negative[0])}`;
    const group = groups.get(key) ?? [];
    group.push(source); groups.set(key, group);
  }
  return [...groups.values()].map(sources => {
    const batteryNodeIds = [...new Set(sources.flatMap(source => source.batteryNodeIds))].sort();
    const evidence = batteryNodeIds.map(id => {
      const node = nodes[id];
      const def = node?.kind === "component" ? defLookup(node.definitionId, node.customDefinition) : undefined;
      return { id, evidence: def ? batteryFaultCurrent(def) : { status: "unknown", reason: "missing-battery-definition" } as ElectricalEvidence };
    });
    const unknownFaultNodeIds = evidence.filter(item => item.evidence.status === "unknown").map(item => item.id);
    let faultCurrent: ElectricalEvidence;
    if (unknownFaultNodeIds.length) faultCurrent = { status: "unknown", reason: "missing-battery-fault-current" };
    else if (sources.some(source => source.levels.length > 1)) {
      // Individual post fault currents do not specify the series bank's
      // equivalent impedance, maximum voltage or fault conditions.
      faultCurrent = { status: "unknown", reason: "series-bank-fault-current" };
    } else {
      const value = evidence.reduce((sum, item) => sum + (item.evidence.status === "unknown" ? 0 : item.evidence.value), 0);
      faultCurrent = evidence.some(item => item.evidence.status === "estimated")
        ? { status: "estimated", value, basis: "ai-estimated" }
        : { status: "known", value, basis: batteryNodeIds.length === 1 ? "declared" : "declared-parallel-sum" };
    }
    const nominalV = sources.every(source => source.nominalV != null)
      ? Math.max(...sources.map(source => source.nominalV!)) : undefined;
    const seriesVerts = [...new Set(sources.flatMap(source => source.levels.slice(0, -1).flatMap(level =>
      level.flatMap(id => {
        const node = nodes[id];
        const def = node?.kind === "component" ? defLookup(node.definitionId, node.customDefinition) : undefined;
        return def?.terminals.filter(t => t.polarity === "positive").map(t => `${id}:${t.id}`) ?? [];
      }))))];
    return { batteryNodeIds, seriesVerts, positiveVerts: [...new Set(sources.flatMap(source => source.positive))],
      negativeVerts: [...new Set(sources.flatMap(source => source.negative))], nominalV, faultCurrent,
      shortCircuitA: faultCurrent.status === "unknown" ? undefined : faultCurrent.value, unknownFaultNodeIds };
  });
}

/** Missing chemistry still limits charge-acceptance checks. Fault-current
 * evidence is separate: choosing a chemistry cannot supply a fault rating. */
export function detectBatteryChemistryUnknown(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const nodesById = indexNodesById(system.nodes);
  // One per bank, not per cell: a 2P8S block of sixteen identical cells is one
  // note, listing every cell.
  for (const stack of computeBatteryStacks(system, defLookup).stacks) {
    const unknown: string[] = [];
    for (const id of stack.batteryNodeIds) {
      const n = nodesById[id];
      if (n?.kind !== "component") continue;
      const def = defLookup(n.definitionId, n.customDefinition);
      if (def && def.chemistry == null && hasUnknownFaultCurrent(def)) unknown.push(id);
    }
    if (unknown.length === 0) continue;
    const first = nodesById[unknown[0]];
    const def =
      first?.kind === "component"
        ? defLookup(first.definitionId, first.customDefinition)
        : undefined;
    const label = (first?.kind === "component" ? first.label : undefined) ?? def?.shortName ?? def?.name ?? unknown[0];
    issues.push({
      code: "battery-chemistry-unknown",
      severity: "info",
      title: { key: "diag.battery-chemistry-unknown.title", vars: { label } },
      message: { key: "diag.battery-chemistry-unknown.message", vars: { label } },
      nodeIds: unknown,
      fix: { key: "diag.battery-chemistry-unknown.fix", vars: { label } },
    });
  }
  return issues;
}

export type FaultProtectionAnalysis = {
  group: BatteryGroup;
  devices: ProtectionDevice[];
  installedFuseRatings: { status: "unknown"; reason: "fuse-product-not-declared" };
  maximumVoltage: { status: "unknown"; reason: "maximum-operating-voltage-not-declared" };
  coordination: { status: "unknown"; reason: "tested-combination-not-declared" };
  placement: { status: "known" } | { status: "unknown"; cableIds: string[] };
  references: { deviceId: string; family: FuseType; product?: string; interruptA?: number; exceedsReference: boolean }[];
};

/** Reference comparisons are inspectable context, never installed-product
 * approval. A cut set alone cannot establish let-through or coordination. */
export function analyzeFaultProtection(system: System, defLookup: DefLookup): FaultProtectionAnalysis[] {
  const topology = buildDcCircuitTopology(system, defLookup);
  const devices = resolveProtectionDevices(system, defLookup);
  return getBatteryGroups(system, defLookup).flatMap(group => {
    const rails = new Set([...group.positiveVerts, ...group.seriesVerts].map(vertex => topology.railId(vertex)));
    const relevant = devices.filter(device => device.terminals.some(vertex => rails.has(topology.railId(vertex))));
    if (!relevant.length) return [];
    return [{ group, devices: relevant,
      installedFuseRatings: { status: "unknown", reason: "fuse-product-not-declared" },
      maximumVoltage: { status: "unknown", reason: "maximum-operating-voltage-not-declared" },
      coordination: { status: "unknown", reason: "tested-combination-not-declared" },
      placement: relevant.some(device => device.unresolvedCableDeclarations.length)
        ? { status: "unknown", cableIds: [...new Set(relevant.flatMap(device => device.unresolvedCableDeclarations))] }
        : { status: "known" },
      references: relevant.flatMap(device => {
        if (!device.family || device.conflicts.length) return [];
        const profile = FUSE_REFERENCE_PROFILES[device.family];
        const interruptA = group.nominalV == null ? undefined : fuseAicAtVoltage(device.family, group.nominalV);
        return [{ deviceId: device.id, family: device.family, product: profile?.product, interruptA,
          exceedsReference: interruptA != null && group.faultCurrent.status !== "unknown" && group.faultCurrent.value > interruptA }];
      }),
    }];
  });
}

/** One actionable evidence request per source pair, rather than one assurance
 * per downstream fuse inferred from chemistry and an unidentified fuse family. */
export function detectFuseAicInadequate(system: System, defLookup: DefLookup): Issue[] {
  const issues: Issue[] = [];
  const nodes = indexNodesById(system.nodes);
  for (const device of resolveProtectionDevices(system, defLookup)) {
    if (!device.conflicts.length) continue;
    const node = nodes[device.nodeIds[0]];
    const label = node?.kind === "component" ? node.label ?? defLookup(node.definitionId, node.customDefinition)?.shortName ?? node.id : device.id;
    issues.push({ code: "fuse-identity-conflict", severity: "warning",
      title: { key: "diag.fuse-identity-conflict.title", vars: { label } },
      message: { key: "diag.fuse-identity-conflict.message", vars: {
        ratings: [...new Set([device.ratingA, ...device.conflicts.map(conflict => conflict.declaredA)].filter(value => value != null))].join(" / "),
      } },
      fix: { key: "diag.fuse-identity-conflict.fix" }, nodeIds: device.nodeIds, cableIds: device.cableIds,
    });
  }
  for (const analysis of analyzeFaultProtection(system, defLookup)) {
    const { group, devices } = analysis;
    const labels = group.batteryNodeIds.map(id => {
      const node = nodes[id];
      const def = node?.kind === "component" ? defLookup(node.definitionId, node.customDefinition) : undefined;
      return node?.kind === "component" ? node.label ?? node.customDefinition?.name ?? def?.shortName ?? def?.name ?? id : id;
    });
    const label = labels.length > 1 ? `${labels[0]} (+${labels.length - 1})` : labels[0];
    const fault = group.faultCurrent;
    const messageKey = fault.status === "unknown"
      ? fault.reason === "source-topology-limit" ? "diag.fuse-protection-unverified.message_topology"
        : fault.reason === "series-bank-fault-current" ? "diag.fuse-protection-unverified.message_series" : "diag.fuse-protection-unverified.message_unknown"
      : fault.status === "estimated" ? "diag.fuse-protection-unverified.message_estimated" : "diag.fuse-protection-unverified.message_declared";
    issues.push({ code: "fuse-protection-unverified", severity: "warning",
      title: { key: "diag.fuse-protection-unverified.title", vars: { label } },
      message: { key: analysis.placement.status === "unknown" ? `${messageKey}_placement` : messageKey, vars: { count: devices.length, faultA: fault.status === "unknown" ? "" : Math.round(fault.value),
        references: analysis.references.filter(reference => reference.exceedsReference).length } },
      fix: { key: "diag.fuse-protection-unverified.fix" },
      nodeIds: [...new Set([...group.batteryNodeIds, ...devices.flatMap(device => device.nodeIds)])],
      cableIds: [...new Set(devices.flatMap(device => device.cableIds))],
    });
  }
  return issues;
}

/** Kept for callers of the old facade. Missing family evidence is included in
 * the grouped protection diagnostic for every bank, without a guessed cutoff. */
export function detectUntypedFuseOnHighFaultBank(_system: System, _defLookup: DefLookup): Issue[] {
  return [];
}

/**
 * A declared rating outside the selected family's published reference range.
 * This does not enumerate every manufacturer's products or approve in-range
 * devices; conductor protection and exact device suitability remain separate.
 */
export function detectFuseRatingUnavailable(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  const nodesById = indexNodesById(system.nodes);

  const report = (
    type: FuseType,
    rating: number,
    ids: { nodeIds?: string[]; cableIds?: string[] },
  ) => {
    const range = FUSE_AMP_RANGE[type];
    if (!range) return;
    // Endpoints only — the family's ladder is deliberately not modelled.
    if (rating >= range.min && rating <= range.max) return;
    const below = rating < range.min;
    issues.push({
      code: "fuse-rating-unavailable",
      severity: "warning",
      title: {
        key: "diag.fuse-rating-unavailable.title",
        vars: { type: fuseTypeDisplayName(type), rating },
      },
      message: {
        key: below
          ? "diag.fuse-rating-unavailable.message_below"
          : "diag.fuse-rating-unavailable.message_above",
        vars: {
          type: fuseTypeDisplayName(type),
          rating,
          min: range.min,
          max: range.max,
        },
      },
      ...ids,
      fix: {
        key: below
          ? "diag.fuse-rating-unavailable.fix_below"
          : "diag.fuse-rating-unavailable.fix_above",
        vars: { type: fuseTypeDisplayName(type), min: range.min, max: range.max },
      },
    });
  };

  for (const n of system.nodes) {
    if (n.kind !== "component" || !n.batteryFuse?.type) continue;
    const amp = n.batteryFuse.ratingA;
    if (typeof amp !== "number" || amp <= 0) continue;
    report(n.batteryFuse.type, amp, { nodeIds: [n.id] });
  }

  // An inline holder's own slot, checked on the node. Its cables can't stand in for it:
  // `resolveCableFuseType` hands the run between a holder and a battery post to the
  // post's family, so a 30 A ANL next to a 40 A MRBF passed as a fine MRBF rating.
  const holderFuse = new Map<string, { type: FuseType; ratingA: number }>();
  for (const n of system.nodes) {
    if (n.kind !== "component" || n.fuseAmp == null || n.fuseAmp <= 0) continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def?.fuseSlotType || !isFuseHolderVariant(def)) continue;
    holderFuse.set(n.id, { type: def.fuseSlotType, ratingA: n.fuseAmp });
    report(def.fuseSlotType, n.fuseAmp, { nodeIds: [n.id] });
  }

  // Dedupes an `inherited` family against the post or holder it came from — on the
  // *rating*, never the node: a legitimate 150 A post fuse would otherwise hide a bad
  // 350 A run off it.
  const postFuseRating = new Map<string, number>();
  for (const n of system.nodes) {
    if (n.kind !== "component" || !n.batteryFuse?.type) continue;
    const amp = n.batteryFuse.ratingA;
    if (typeof amp === "number") postFuseRating.set(n.id, amp);
  }

  for (const cable of system.cables) {
    const rating = cable.fuseAmpOverride;
    if (rating == null || rating <= 0) continue;
    const resolved = resolveCableFuseType(cable, system.nodes, defLookup);
    if (!resolved) continue;
    if (resolved.kind === "inherited") {
      const postRating =
        postFuseRating.get(cable.fromNodeId) ?? postFuseRating.get(cable.toNodeId);
      if (postRating != null && postRating === rating) continue;
      const holder = holderFuse.get(cable.fromNodeId) ?? holderFuse.get(cable.toNodeId);
      if (holder && holder.type === resolved.type && holder.ratingA === rating) continue;
    }
    const node = nodesById[cable.fromNodeId] ?? nodesById[cable.toNodeId];
    report(resolved.type, rating, {
      cableIds: [cable.id],
      nodeIds: node ? [node.id] : undefined,
    });
  }

  return issues;
}
